/**
 * MedicalService.js - PRODUCTION VERSION v2.0.2
 * Medical facility extraction, MTF location management, and evacuation routing.
 *
 * VERSION 2.0.1 - ESLint compliance fix (February 13, 2026)
 * Build Date: February 13, 2026
 *
 * v2.0.2 CHANGES (March 12, 2026):
 * - BUG-087: icu_beds not copied in extractMedicalFacilities() — added to all 5 Role 2E
 *   mapping blocks (default 10) and all 3 Role 3 mapping blocks (default 40). Field was
 *   correctly set by ForceService and serialized by BaseMedicalFacility.toJSON() but
 *   silently dropped when MedicalService mapped source objects to plain facility objects.
 *
 * v2.0.1 CHANGES (February 13, 2026):
 * - FIXED: ESLint error "import/no-anonymous-default-export"
 * - CHANGED: Line 1469-1478 - Assign object to variable before export
 * - RESULT: Zero ESLint warnings, production-ready
 * 
 * v2.0 CHANGES (February 11, 2026):
 * - ADDED: extractMedicalFacilities(forceData) - rich facility extraction for Tab2/Tab3
 * - ADDED: calculateEvacuationDistances(facilities, defaultTerrain) - evacuation routing
 * - ADDED: recalculateFacilityEvacuation(facility, newValues, allFacilities) - user-edit recalc
 * - Ported all helper functions from Tab3 inline definitions:
 *     getFacilityId, getBedDefault, CSS_UNIT_TYPES, brigadeHasRole1,
 *     sanitizeFacilityName, getOrdinal, extractLocalUnitName,
 *     generateFacilityDisplayName, getDefaultPositionForRole
 * - All console.log converted to debugLog (DEBUG=false in production)
 * - All console.warn converted to debugWarn
 * - Legacy console.error kept for critical production errors
 * - Tab3 inline copies of these functions unchanged (backward compatible)
 *
 * v1.2 CHANGES (January 18, 2026):
 * - UTIL-002 FIX: Removed excessive console.log statements
 * - Added DEBUG flag for conditional logging (default: false)
 * - Production-ready: No console spam
 *
 * v1.1 CHANGES (November 30, 2025):
 * - Fixed: Corps uses `role3_corps_facility` not `role3_facility`
 * - Added: Defensive null checks everywhere
 * - Added: Support for both Tab1 custom structure AND ForceBuilder structure
 *
 * Author: Dr. Tatar Radu MD
 * Date: February 13, 2026
 * Version: 2.2.1 - BUG-117: generateFacilityDisplayName now uses battalionIdx as authoritative ordinal — prevents all battalions resolving to "1st Bn" when sub_unit names don't match parse patterns (March 16, 2026)
 *
 * Version: 2.2.0 - ENH-006: Dynamic MTF activation fields (March 15, 2026)
 *   All 18 facility mapping blocks now include:
 *     activate_day:   src.activate_day   ?? null   (null = active from Day 1)
 *     deactivate_day: src.deactivate_day ?? null   (null = active until end of op)
 *     setup_hours:    src.setup_hours    ?? null   (null = instant, full capacity Day 1)
 *   casualtyCalculator.js reads these per-day to determine operational status.
 *   Tab3 v4.23.0 exposes the UI to set them per facility.
 *
 * Version: 2.1.0 - ENH-002: attached_assets/detached_assets initialised to [] (March 15, 2026)
 *
 * Version: 2.0.1 - ESLint compliance fix
 */

// ============================================================================
// IMPORTS
// ============================================================================

// MTF Role Definitions - for beds, FEBA distances (used by getBedDefault, getFEBADefault)
import {
  ROLE_1_DEFINITION,
  ROLE_2_BASIC_DEFINITION,
  ROLE_2_ENHANCED_DEFINITION,
  ROLE_3_DEFINITION,
  ROLE_4_DEFINITION
} from '../constants/mtfCapabilities';

// Terrain and evacuation time factors (used by calculateEvacuationDistances)
import {
  getTerrainFactor,
  calculateGroundEvacuationTime
} from '../constants/terrainFactors';

// ============================================================================
// DEBUG FLAG - Set to true during development, false for production
// ============================================================================
const DEBUG = false;

// Debug logging helpers - only log when DEBUG is true
const debugLog = (...args) => { if (DEBUG) console.log(...args); };
const debugWarn = (...args) => { if (DEBUG) console.warn(...args); };

// ============================================================================
// SECTION 1: EXISTING EXPORTS (v1.x - unchanged)
// ============================================================================

/**
 * Extract MTF locations from forceBuilderResult.
 * Lightweight grouped format for the casualty calculator's routing logic.
 * Returns: { role1: [], role2_basic: [], role2_enhanced: [], role3: null, role4: {...} }
 *
 * @param {Object} forceBuilderResult - Result from Tab1 ForceBuilder
 * @param {string} echelon - Force echelon (BATTALION, BRIGADE, DIVISION, CORPS)
 * @returns {Object|null} MTF locations organized by role
 */
export function extractMTFLocations(forceBuilderResult, echelon) {
  debugLog('🔍 MTF Extractor called:', { echelon, hasResult: !!forceBuilderResult });

  if (!forceBuilderResult) {
    debugWarn('⚠️ MTF Extractor: No forceBuilderResult provided');
    return null;
  }

  const mtfLocations = {
    role1: [],
    role2_basic: [],
    role2_enhanced: [],
    role3: null,
    role4: {
      distance_km: 300,
      managed_by: 'NATIONAL_LEVEL',
      note: 'Strategic evacuation - not managed at corps/division/brigade level'
    }
  };

  try {
    switch (echelon) {
      case 'BATTALION':
        extractBattalionMTFs(forceBuilderResult, mtfLocations);
        break;
      case 'BRIGADE':
        extractBrigadeMTFs(forceBuilderResult, mtfLocations);
        break;
      case 'DIVISION':
        extractDivisionMTFs(forceBuilderResult, mtfLocations);
        break;
      case 'CORPS':
        extractCorpsMTFs(forceBuilderResult, mtfLocations);
        break;
      default:
        debugWarn(`⚠️ Unknown echelon: ${echelon}`);
    }

    debugLog('📊 MTF Extraction Summary:', {
      role1_count: mtfLocations.role1.length,
      role2_basic_count: mtfLocations.role2_basic.length,
      role2_enhanced_count: mtfLocations.role2_enhanced.length,
      has_role3: !!mtfLocations.role3
    });

  } catch (error) {
    console.error('❌ MTF Extraction Error:', error);
    return null;
  }

  return mtfLocations;
}

function extractBattalionMTFs(result, mtfLocations) {
  if (result.role1) {
    mtfLocations.role1.push({
      facility_id: result.role1.facility_id,
      facility_name: result.role1.facility_name || 'Battalion Aid Station',
      parent_battalion_id: result.battalion?.id || null,
      parent_brigade_id: null,
      parent_division_id: null,
      echelon: 'BATTALION',
      position_feba_km: result.role1.position_feba_km || 2,
      bed_capacity: result.role1.beds || 10
    });
  }
}

function extractBrigadeMTFs(result, mtfLocations) {
  if (result.role1_facilities && Array.isArray(result.role1_facilities)) {
    result.role1_facilities.forEach(role1 => {
      mtfLocations.role1.push({
        facility_id: role1.facility_id,
        facility_name: role1.facility_name || 'Battalion Aid Station',
        parent_battalion_id: role1.parent_battalion_id || null,
        parent_brigade_id: role1.parent_brigade_id || result.brigade?.id || null,
        parent_division_id: null,
        echelon: 'BATTALION',
        position_feba_km: role1.position_feba_km || 2,
        bed_capacity: role1.beds || 10
      });
    });
  }

  if (result.role2) {
    mtfLocations.role2_basic.push({
      facility_id: result.role2.facility_id,
      facility_name: result.role2.facility_name || 'Brigade Role 2 Basic',
      parent_brigade_id: result.brigade?.id || null,
      parent_division_id: null,
      echelon: 'BRIGADE',
      position_feba_km: result.role2.position_feba_km || 15,
      bed_capacity: result.role2.beds || 50,
      surgical_teams: result.role2.surgical_teams || 2
    });
  }
}

function extractDivisionMTFs(result, mtfLocations) {
  if (result.role1_facilities && Array.isArray(result.role1_facilities)) {
    result.role1_facilities.forEach(role1 => {
      mtfLocations.role1.push({
        facility_id: role1.facility_id,
        facility_name: role1.facility_name || 'Battalion Aid Station',
        parent_battalion_id: role1.parent_battalion_id || null,
        parent_brigade_id: role1.parent_brigade_id || null,
        parent_division_id: result.division?.id || null,
        echelon: 'BATTALION',
        position_feba_km: role1.position_feba_km || 2,
        bed_capacity: role1.beds || 10
      });
    });
  }

  if (result.role2_facilities && Array.isArray(result.role2_facilities)) {
    result.role2_facilities.forEach(role2 => {
      const isEnhanced = role2.icu_beds || role2.parent_division_id;
      if (isEnhanced) {
        mtfLocations.role2_enhanced.push({
          facility_id: role2.facility_id,
          facility_name: role2.facility_name || 'Division Role 2 Enhanced',
          parent_division_id: role2.parent_division_id || result.division?.id || null,
          echelon: 'DIVISION',
          position_feba_km: role2.position_feba_km || 50,
          bed_capacity: role2.beds || 72,
          surgical_teams: role2.surgical_teams || 3,
          icu_beds: role2.icu_beds || 12
        });
      } else {
        mtfLocations.role2_basic.push({
          facility_id: role2.facility_id,
          facility_name: role2.facility_name || 'Brigade Role 2 Basic',
          parent_brigade_id: role2.parent_brigade_id || null,
          parent_division_id: result.division?.id || null,
          echelon: 'BRIGADE',
          position_feba_km: role2.position_feba_km || 15,
          bed_capacity: role2.beds || 50,
          surgical_teams: role2.surgical_teams || 2
        });
      }
    });
  }

  if (result.division?.role2_enhanced_facility) {
    const r2e = result.division.role2_enhanced_facility;
    const exists = mtfLocations.role2_enhanced.find(mtf => mtf.facility_id === r2e.facility_id);
    if (!exists) {
      mtfLocations.role2_enhanced.push({
        facility_id: r2e.facility_id,
        facility_name: r2e.facility_name || 'Division Role 2 Enhanced',
        parent_division_id: result.division.id,
        echelon: 'DIVISION',
        position_feba_km: r2e.position_feba_km || 50,
        bed_capacity: r2e.beds || 72,
        surgical_teams: r2e.surgical_teams || 3,
        icu_beds: r2e.icu_beds || 12
      });
    }
  }
}

function extractCorpsMTFs(result, mtfLocations) {
  if (result.role1_facilities && Array.isArray(result.role1_facilities)) {
    result.role1_facilities.forEach(role1 => {
      mtfLocations.role1.push({
        facility_id: role1.facility_id,
        facility_name: role1.facility_name || 'Battalion Aid Station',
        parent_battalion_id: role1.parent_battalion_id || null,
        parent_brigade_id: role1.parent_brigade_id || null,
        parent_division_id: role1.parent_division_id || null,
        echelon: 'BATTALION',
        position_feba_km: role1.position_feba_km || 2,
        bed_capacity: role1.beds || 10
      });
    });
  }

  if (result.role2_facilities && Array.isArray(result.role2_facilities)) {
    result.role2_facilities.forEach(role2 => {
      const isEnhanced = role2.icu_beds || role2.parent_division_id;
      if (isEnhanced) {
        mtfLocations.role2_enhanced.push({
          facility_id: role2.facility_id,
          facility_name: role2.facility_name || 'Division Role 2 Enhanced',
          parent_division_id: role2.parent_division_id || null,
          echelon: 'DIVISION',
          position_feba_km: role2.position_feba_km || 50,
          bed_capacity: role2.beds || 72,
          surgical_teams: role2.surgical_teams || 3,
          icu_beds: role2.icu_beds || 12
        });
      } else {
        mtfLocations.role2_basic.push({
          facility_id: role2.facility_id,
          facility_name: role2.facility_name || 'Brigade Role 2 Basic',
          parent_brigade_id: role2.parent_brigade_id || null,
          parent_division_id: role2.parent_division_id || null,
          echelon: 'BRIGADE',
          position_feba_km: role2.position_feba_km || 15,
          bed_capacity: role2.beds || 50,
          surgical_teams: role2.surgical_teams || 2
        });
      }
    });
  }

  if (result.divisions && Array.isArray(result.divisions)) {
    result.divisions.forEach(division => {
      if (division.role2_enhanced_facility) {
        const r2e = division.role2_enhanced_facility;
        const exists = mtfLocations.role2_enhanced.find(mtf => mtf.facility_id === r2e.facility_id);
        if (!exists) {
          mtfLocations.role2_enhanced.push({
            facility_id: r2e.facility_id,
            facility_name: r2e.facility_name || `${division.name} Role 2 Enhanced`,
            parent_division_id: division.id,
            echelon: 'DIVISION',
            position_feba_km: r2e.position_feba_km || 50,
            bed_capacity: r2e.beds || 72,
            surgical_teams: r2e.surgical_teams || 3,
            icu_beds: r2e.icu_beds || 12
          });
        }
      }
    });
  }

  if (result.role3_facility) {
    const r3 = result.role3_facility;
    mtfLocations.role3 = {
      facility_id: r3.facility_id,
      facility_name: r3.facility_name || 'Corps Role 3 Hospital',
      parent_corps_id: result.corps?.id || r3.parent_corps_id,
      echelon: 'CORPS',
      position_feba_km: r3.position_feba_km || 100,
      bed_capacity: r3.beds || 200,
      surgical_teams: r3.surgical_teams || 10,
      icu_beds: r3.icu_beds || 40,
      specialized_care: r3.specialized_care || true
    };
  } else {
    debugWarn('⚠️ No Corps Role 3 facility found');
  }
}

/**
 * Get evacuation destination for a unit (1 echelon up the chain).
 *
 * @param {Object} mtfLocations - MTF locations from extractMTFLocations
 * @param {string} unitType - 'battalion', 'brigade', 'division', 'corps'
 * @param {string} unitId - ID of the unit
 * @returns {Object|null} Evacuation destination MTF with distance
 */
export function getEvacuationDestination(mtfLocations, unitType, unitId) {
  if (!mtfLocations) {
    debugWarn('⚠️ getEvacuationDestination: No mtfLocations provided');
    return null;
  }

  try {
    switch (unitType.toLowerCase()) {
      case 'battalion': {
        const battalion = mtfLocations.role1.find(r1 => r1.parent_battalion_id === unitId);
        if (battalion?.parent_brigade_id) {
          const destination = mtfLocations.role2_basic.find(
            r2 => r2.parent_brigade_id === battalion.parent_brigade_id
          );
          if (destination) {
            return {
              ...destination,
              distance_km: destination.position_feba_km - battalion.position_feba_km
            };
          }
        }
        break;
      }

      case 'brigade': {
        const brigade = mtfLocations.role2_basic.find(r2 => r2.parent_brigade_id === unitId);
        if (brigade?.parent_division_id) {
          const destination = mtfLocations.role2_enhanced.find(
            r2e => r2e.parent_division_id === brigade.parent_division_id
          );
          if (destination) {
            return {
              ...destination,
              distance_km: destination.position_feba_km - brigade.position_feba_km
            };
          }
        }
        break;
      }

      case 'division': {
        const division = mtfLocations.role2_enhanced.find(r2e => r2e.parent_division_id === unitId);
        if (division && mtfLocations.role3) {
          return {
            ...mtfLocations.role3,
            distance_km: mtfLocations.role3.position_feba_km - division.position_feba_km
          };
        }
        break;
      }

      case 'corps':
        return mtfLocations.role4;

      default:
        debugWarn(`⚠️ Unknown unit type: ${unitType}`);
        return null;
    }
  } catch (error) {
    console.error('❌ Error in getEvacuationDestination:', error);
    return null;
  }

  return null;
}

/**
 * Get subordinate MTFs (2 echelons down).
 *
 * @param {Object} mtfLocations - MTF locations from extractMTFLocations
 * @param {string} unitType - 'brigade', 'division', 'corps'
 * @param {string} unitId - ID of the unit
 * @returns {Object} Subordinate MTFs by echelon
 */
export function getSubordinateMTFs(mtfLocations, unitType, unitId) {
  if (!mtfLocations) {
    debugWarn('⚠️ getSubordinateMTFs: No mtfLocations provided');
    return null;
  }

  const subordinates = { battalions: [], brigades: [], divisions: [] };

  try {
    switch (unitType.toLowerCase()) {
      case 'brigade':
        subordinates.battalions = mtfLocations.role1.filter(
          r1 => r1.parent_brigade_id === unitId
        );
        break;

      case 'division':
        subordinates.brigades = mtfLocations.role2_basic.filter(
          r2 => r2.parent_division_id === unitId
        );
        subordinates.battalions = mtfLocations.role1.filter(
          r1 => r1.parent_division_id === unitId
        );
        break;

      case 'corps':
        subordinates.divisions = mtfLocations.role2_enhanced;
        subordinates.brigades = mtfLocations.role2_basic;
        break;

      default:
        debugWarn(`⚠️ Unknown unit type: ${unitType}`);
    }
  } catch (error) {
    console.error('❌ Error in getSubordinateMTFs:', error);
  }

  return subordinates;
}

// ============================================================================
// SECTION 2: HELPER FUNCTIONS (v2.0 - support for extractMedicalFacilities)
// ============================================================================

/**
 * Generate a simple unique ID string.
 */
const generateId = () => Math.random().toString(36).substr(2, 9);

/**
 * Safely extract facility ID from a facility object.
 * ForceBuilder uses 'facility_id'; serialized objects may use 'id'.
 * Generates a new ID as fallback if neither exists.
 *
 * @param {Object} facility - Facility object from ForceBuilder
 * @param {string} prefix - Prefix for generated ID (e.g., 'role1', 'role2')
 * @returns {string} Facility ID
 */
const getFacilityId = (facility, prefix = 'facility') => {
  if (!facility) {
    debugWarn(`[!] getFacilityId: null facility passed, generating ID with prefix "${prefix}"`);
    return `${prefix}_${generateId()}`;
  }
  if (facility.facility_id) return facility.facility_id;
  if (facility.id) return facility.id;
  debugWarn(`[!] getFacilityId: No ID found on facility (prefix: "${prefix}"), generating new ID`);
  return `${prefix}_${generateId()}`;
};

/**
 * Return bed count default for a given role type.
 * Uses imported mtfCapabilities constants.
 */
const getBedDefault = (roleType) => {
  switch (roleType) {
    case 'ROLE1':   return ROLE_1_DEFINITION?.holding_capacity?.beds || 6;
    case 'ROLE2_BASIC':
    case 'ROLE2B':  return ROLE_2_BASIC_DEFINITION?.holding_capacity?.beds || 30;
    case 'ROLE2_ENHANCED':
    case 'ROLE2E':  return ROLE_2_ENHANCED_DEFINITION?.holding_capacity?.beds || 40;
    case 'ROLE3':   return ROLE_3_DEFINITION?.holding_capacity?.beds || 150;
    case 'ROLE4':   return ROLE_4_DEFINITION?.holding_capacity?.beds || 500;
    default:        return 10;
  }
};

/**
 * CSS (Combat Service Support) unit types that do NOT have organic Role 1 facilities.
 * When a brigade is changed to one of these types in Tab1, its Role 1/2B facilities
 * are filtered out during extraction.
 */
const CSS_UNIT_TYPES = ['MEDICAL', 'LOGISTICS', 'SIGNAL', 'HQ', 'MAINTENANCE', 'TRANSPORT'];

/**
 * Returns true if the unit type should have Role 1 facilities.
 *
 * @param {string} unitType
 * @returns {boolean}
 */
const brigadeHasRole1 = (unitType) => {
  if (!unitType) return true; // Default: show if unknown
  return !CSS_UNIT_TYPES.includes(unitType.toUpperCase());
};

/**
 * Remove "undefined/" or "undefined " prefixes from facility names.
 *
 * @param {string} name
 * @returns {string}
 */
const sanitizeFacilityName = (name) => {
  if (!name) return 'Medical Facility';
  let sanitized = name.replace(/^undefined\//i, '');
  sanitized = sanitized.replace(/^undefined\s+/i, '');
  return sanitized || 'Medical Facility';
};

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.).
 *
 * @param {number} n
 * @returns {string}
 */
const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/**
 * Extract a clean local unit identifier from a ForceBuilder hierarchical name.
 * Parses names like "3/1st CORPS Division 1 Brigade 1 Armor" → { shortName: "3rd Bn (Armor)" }
 *
 * @param {string} fullName - Full hierarchical name from ForceBuilder
 * @param {string} unitLevel - 'battalion', 'brigade', 'division', or 'corps'
 * @returns {Object} { number, ordinal, type, shortName }
 */
const extractLocalUnitName = (fullName, unitLevel) => {
  if (!fullName) {
    return { number: 1, ordinal: '1st', type: null, shortName: `${unitLevel} 1` };
  }

  const name = sanitizeFacilityName(fullName);

  if (unitLevel === 'battalion') {
    const slashMatch = name.match(/^(\d+)\//);
    if (slashMatch) {
      const num = parseInt(slashMatch[1]);
      const typeMatch = name.match(/\b(Armor|Infantry|Mechanized|Artillery|Cavalry|Engineers|Signal|Logistics|Medical|Aviation|Air Defense|Airborne|Mountain)\s*$/i);
      const unitType = typeMatch ? typeMatch[1] : null;
      return {
        number: num,
        ordinal: getOrdinal(num),
        type: unitType,
        shortName: unitType ? `${getOrdinal(num)} Bn (${unitType})` : `${getOrdinal(num)} Bn`
      };
    }
    const bnMatch = name.match(/(?:Battalion|Bn)\s*(\d+)/i);
    if (bnMatch) {
      const num = parseInt(bnMatch[1]);
      const typeMatch = name.match(/\b(Armor|Infantry|Mechanized|Artillery|Cavalry|Engineers|Signal|Logistics|Medical|Aviation|Air Defense|Airborne|Mountain)\s*$/i);
      const unitType = typeMatch ? typeMatch[1] : null;
      return {
        number: num,
        ordinal: getOrdinal(num),
        type: unitType,
        shortName: unitType ? `${getOrdinal(num)} Bn (${unitType})` : `${getOrdinal(num)} Bn`
      };
    }
  }

  if (unitLevel === 'brigade') {
    const bdeMatch = name.match(/(?:Brigade|Bde)\s*(\d+)/i);
    if (bdeMatch) {
      const num = parseInt(bdeMatch[1]);
      const typeMatch = name.match(/\b(Mechanized|Armor|Infantry|Artillery|Logistics|Medical|Sustainment|Mountain|Airborne|Air Assault|Stryker)\s*(?:Brigade|Bde)?/i);
      const unitType = typeMatch ? typeMatch[1] : null;
      return {
        number: num,
        ordinal: getOrdinal(num),
        type: unitType,
        shortName: unitType ? `${getOrdinal(num)} Bde (${unitType})` : `${getOrdinal(num)} Bde`
      };
    }
  }

  if (unitLevel === 'division') {
    const divMatch = name.match(/(?:Division|Div)\s*(\d+)/i);
    if (divMatch) {
      const num = parseInt(divMatch[1]);
      return {
        number: num,
        ordinal: getOrdinal(num),
        type: null,
        shortName: `${getOrdinal(num)} Div`
      };
    }
  }

  if (unitLevel === 'corps') {
    const corpsMatch = name.match(/(\d+)(?:st|nd|rd|th)?\s*(?:CORPS|Corps)/i);
    if (corpsMatch) {
      const num = parseInt(corpsMatch[1]);
      return {
        number: num,
        ordinal: getOrdinal(num),
        type: null,
        shortName: `${getOrdinal(num)} Corps`
      };
    }
    if (name.toLowerCase().includes('corps')) {
      return { number: 1, ordinal: '1st', type: null, shortName: 'Corps' };
    }
  }

  return { number: 1, ordinal: '1st', type: null, shortName: name };
};

/**
 * Generate a human-readable facility display name.
 * Uses extractLocalUnitName for clean, non-redundant names.
 *
 * Examples:
 *   ROLE1:          "3rd Bn (Armor) Aid Station - 1st Bde"
 *   ROLE2_BASIC:    "1st Bde Medical Company - 2nd Div"
 *   ROLE2_ENHANCED: "1st Div Role 2E"
 *   ROLE3:          "1st Corps Role 3"
 *
 * @param {Object} facility - Facility object with hierarchy data
 * @param {string} facilityType - ROLE1, ROLE2_BASIC, ROLE2_ENHANCED, ROLE3, ROLE4
 * @param {Object} options - Context: { battalionIdx, brigadeIdx, divisionIdx }
 * @returns {string} Display name
 */
const generateFacilityDisplayName = (facility, facilityType, options = {}) => {
  const { battalionIdx, brigadeIdx, divisionIdx } = options;

  // BUG-117 FIX (March 16, 2026): When battalionIdx is provided (always the case for the
  // DIVISION path), use it as the authoritative battalion number rather than parsing the
  // sub_unit name. Sub_unit names from generateSubUnitsFromDoctrine() use doctrinal name
  // patterns (e.g. "1st DIVISION Brigade 1 Mechanized") that extractLocalUnitName cannot
  // parse — all fall through to the default ordinal "1st", making every battalion show
  // as "1st Bn Aid Station". Fix: when battalionIdx is available, build a clean
  // "Battalion N" string instead so parsing always succeeds with a unique ordinal.
  const rawBattalionName =
    (battalionIdx !== undefined ? `Battalion ${battalionIdx + 1}` : null) ||
    facility?.parent_battalion_name ||
    null;
  const rawBrigadeName = facility?.parent_brigade_name ||
    (brigadeIdx !== undefined ? `Brigade ${brigadeIdx + 1}` : null);
  const rawDivisionName = facility?.parent_division_name ||
    (divisionIdx !== undefined ? `Division ${divisionIdx + 1}` : null);
  const rawCorpsName = facility?.parent_corps_name || 'Corps';

  const battalion = rawBattalionName ? extractLocalUnitName(rawBattalionName, 'battalion') : null;
  const brigade   = rawBrigadeName   ? extractLocalUnitName(rawBrigadeName,   'brigade')   : null;
  const division  = rawDivisionName  ? extractLocalUnitName(rawDivisionName,  'division')  : null;
  const corps     = extractLocalUnitName(rawCorpsName, 'corps');

  const sanitizedFacilityName = facility?.facility_name
    ? sanitizeFacilityName(facility.facility_name)
    : null;

  const isFromMedicalBrigade = facility?.is_from_medical_brigade ||
    facility?.source_medical_brigade ||
    rawBrigadeName?.toLowerCase().includes('medical');

  switch (facilityType) {
    case 'ROLE1':
      if (battalion && brigade) return `${battalion.shortName} Aid Station - ${brigade.shortName}`;
      if (battalion) return `${battalion.shortName} Aid Station`;
      return sanitizedFacilityName || 'Battalion Aid Station';

    case 'ROLE2_BASIC':
      if (brigade && division) return `${brigade.shortName} Medical Company - ${division.shortName}`;
      if (brigade) return `${brigade.shortName} Medical Company`;
      return sanitizedFacilityName || 'Brigade Medical Company';

    case 'ROLE2_ENHANCED':
      if (isFromMedicalBrigade && division) return `Med Bde Role 2E - ${division.shortName}`;
      if (division) return `${division.shortName} Role 2E`;
      return sanitizedFacilityName || 'Division Role 2 Enhanced';

    case 'ROLE3':
      if (isFromMedicalBrigade && division) return `Med Bde Role 3 - ${division.shortName}`;
      if (corps && corps.shortName !== 'Corps') return `${corps.shortName} Role 3`;
      return sanitizedFacilityName || 'Corps Role 3 Hospital';

    case 'ROLE4':
      return sanitizedFacilityName || 'Role 4 Strategic Hospital';

    default:
      return sanitizedFacilityName || 'Medical Facility';
  }
};

/**
 * Return the default FEBA distance for a facility role type.
 * Based on NATO planning factors.
 *
 * @param {string} roleType - 'ROLE1', 'ROLE2_BASIC', 'ROLE2_ENHANCED', 'ROLE3', 'ROLE4'
 * @returns {number} Default distance in km from FEBA
 */
const getDefaultPositionForRole = (roleType) => {
  const defaults = {
    ROLE1: 5,
    ROLE2_BASIC: 20,
    ROLE2_ENHANCED: 50,
    ROLE3: 100,
    ROLE4: 300
  };
  return defaults[roleType] || 20;
};

// ============================================================================
// SECTION 3: NEW EXPORTS (v2.0)
// ============================================================================

/**
 * Extract medical facilities from the ForceBuilder force structure.
 * Processes the hierarchical force structure and returns all medical facilities
 * as a flat array of standardized facility objects.
 *
 * This is the rich extraction used by Tab2 (buildUpdates → mtf_hierarchy) and
 * Tab3 (facility display panel). Tab3 keeps its own inline copy for backward
 * compatibility; this version is the authoritative service-layer implementation.
 *
 * @param {Object} forceData - Force structure from scenario.force (has .forceBuilderResult, .echelon)
 * @returns {Array} Flat array of facility objects with standardized structure
 */
export function extractMedicalFacilities(forceData) {
  const facilities = [];
  const result = forceData?.forceBuilderResult;
  const echelon = forceData?.echelon;

  if (!result) return facilities;

  try {
    // ---- BATTALION: 1 Role 1 facility ----------------------------------------
    if (echelon === 'BATTALION' && result.role1) {
      const battalionName = result.battalion?.name || 'Battalion';
      facilities.push({
        id: getFacilityId(result.role1, 'role1_bn'),
        name: generateFacilityDisplayName({ ...result.role1, parent_battalion_name: battalionName }, 'ROLE1'),
        type: 'ROLE1',
        parent_unit: battalionName,
        parent_unit_id: result.battalion?.unit_id || result.battalion?.id,
        beds: result.role1.beds || getBedDefault('ROLE1'),
        personnel: result.role1.personnel || 15,
        has_surgical: false,
        hierarchy: { battalion: battalionName },
        organic_assets:    result.role1.organic_assets    || null,
        attached_assets:   result.role1.attached_assets   || [],
        detached_assets:   result.role1.detached_assets   || [],
        evacuation_config: result.role1.evacuation_config || null,
        activate_day:    result.role1.activate_day   ?? null,
        deactivate_day:  result.role1.deactivate_day ?? null,
        setup_hours:     result.role1.setup_hours    ?? null
      });
    }

    // ---- BRIGADE: Multiple Role 1 + 1 Role 2 Basic ---------------------------
    else if (echelon === 'BRIGADE' && result.role1_facilities) {
      const brigadeName = result.brigade?.name || 'Brigade';
      const battalions  = result.battalions || [];
      const brigade     = result.brigade;
      const hasSubUnits = brigade?.sub_units && Array.isArray(brigade.sub_units) && brigade.sub_units.length > 0;

      if (hasSubUnits) {
        // v3.0 path: use doctrinal sub-units
        const subUnitsNeedingRole1 = brigade.sub_units.filter(su =>
          !['HQ', 'SIGNAL', 'MEDICAL'].includes(su.unit_type)
        );

        result.role1_facilities.forEach((r1, idx) => {
          const subUnit = subUnitsNeedingRole1[idx];
          if (subUnit) {
            facilities.push({
              id: getFacilityId(r1, `role1_bde_${subUnit.id || idx}`),
              name: generateFacilityDisplayName(
                { ...r1, parent_battalion_name: subUnit.name, parent_brigade_name: brigadeName },
                'ROLE1', { battalionIdx: idx }
              ),
              type: 'ROLE1',
              parent_unit: subUnit.name,
              parent_unit_type: subUnit.unit_type,
              parent_unit_id: subUnit.id,
              parent_brigade: brigadeName,
              beds: r1.beds || getBedDefault('ROLE1'),
              personnel: r1.personnel || 15,
              has_surgical: false,
              hierarchy: { brigade: brigadeName, battalion: subUnit.name },
              sub_unit_strength: subUnit.strength,
              sub_unit_type: subUnit.unit_type,
              is_from_doctrinal_composition: true,
              organic_assets:    r1.organic_assets    || null,
              attached_assets:   r1.attached_assets   || [],
              detached_assets:   r1.detached_assets   || [],
              evacuation_config: r1.evacuation_config || null,
        activate_day:    r1.activate_day   ?? null,
        deactivate_day:  r1.deactivate_day ?? null,
        setup_hours:     r1.setup_hours    ?? null
            });
          } else {
            // More Role 1s than sub-units — defensive
            debugWarn(`extractMedicalFacilities: Role 1 facility ${idx} has no matching sub-unit in brigade "${brigadeName}"`);
            facilities.push({
              id: getFacilityId(r1, `role1_bde_orphan${idx}`),
              name: `${brigadeName} Aid Station ${idx + 1} (Orphaned)`,
              type: 'ROLE1',
              parent_unit: `Unknown Unit ${idx + 1}`,
              parent_brigade: brigadeName,
              beds: r1.beds || getBedDefault('ROLE1'),
              personnel: r1.personnel || 15,
              has_surgical: false,
              hierarchy: { brigade: brigadeName, battalion: `Unknown ${idx + 1}` },
              is_orphaned: true,
              organic_assets:    r1.organic_assets    || null,
              attached_assets:   r1.attached_assets   || [],
              detached_assets:   r1.detached_assets   || [],
              evacuation_config: r1.evacuation_config || null,
        activate_day:    r1.activate_day   ?? null,
        deactivate_day:  r1.deactivate_day ?? null,
        setup_hours:     r1.setup_hours    ?? null
            });
          }
        });

      } else {
        // Legacy path: brigade without sub_units
        result.role1_facilities.forEach((r1, idx) => {
          const parentBattalion = battalions[idx];
          const battalionName = parentBattalion?.name || r1.parent_battalion_name || `Battalion ${idx + 1}`;
          facilities.push({
            id: getFacilityId(r1, `role1_bde_bn${idx + 1}`),
            name: generateFacilityDisplayName(
              { ...r1, parent_battalion_name: battalionName, parent_brigade_name: brigadeName },
              'ROLE1', { battalionIdx: idx }
            ),
            type: 'ROLE1',
            parent_unit: battalionName,
            parent_unit_id: parentBattalion?.unit_id || parentBattalion?.id,
            parent_brigade: brigadeName,
            beds: r1.beds || getBedDefault('ROLE1'),
            personnel: r1.personnel || 15,
            has_surgical: false,
            hierarchy: { brigade: brigadeName, battalion: battalionName },
            is_from_doctrinal_composition: false,
            organic_assets:    r1.organic_assets    || null,
            attached_assets:   r1.attached_assets   || [],
            detached_assets:   r1.detached_assets   || [],
            evacuation_config: r1.evacuation_config || null,
        activate_day:    r1.activate_day   ?? null,
        deactivate_day:  r1.deactivate_day ?? null,
        setup_hours:     r1.setup_hours    ?? null
          });
        });
      }

      // Role 2 Basic (brigade level)
      if (result.role2) {
        facilities.push({
          id: getFacilityId(result.role2, 'role2_basic_bde'),
          name: generateFacilityDisplayName(
            { ...result.role2, parent_brigade_name: brigadeName }, 'ROLE2_BASIC'
          ),
          type: 'ROLE2_BASIC',
          parent_unit: brigadeName,
          parent_unit_id: result.brigade?.unit_id || result.brigade?.id,
          beds: result.role2.beds || getBedDefault('ROLE2B'),
          personnel: result.role2.personnel || 75,
          surgical_teams: result.role2.surgical_teams || 2,
          has_surgical: true,
          hierarchy: { brigade: brigadeName, battalion: null },
          organic_assets:    result.role2.organic_assets    || null,
          attached_assets:   result.role2.attached_assets   || [],
          detached_assets:   result.role2.detached_assets   || [],
          evacuation_config: result.role2.evacuation_config || null,
        activate_day:    result.role2.activate_day   ?? null,
        deactivate_day:  result.role2.deactivate_day ?? null,
        setup_hours:     result.role2.setup_hours    ?? null
        });
      }
    }

    // ---- DIVISION: Role 1s + Role 2 Basics + Role 2 Enhanced ----------------
    else if (echelon === 'DIVISION' && result.role1_facilities) {
      const divisionName = result.division?.name || 'Division';
      const brigades     = result.brigades || [];
      const battalions   = result.battalions || [];
      let role1Index = 0;

      brigades.forEach((brigade, brigadeIdx) => {
        const bdeNameLocal = brigade.name || `Brigade ${brigadeIdx + 1}`;
        const hasSubUnits  = brigade.sub_units && Array.isArray(brigade.sub_units) && brigade.sub_units.length > 0;

        if (hasSubUnits) {
          const subUnitsNeedingRole1 = brigade.sub_units.filter(su =>
            !['HQ', 'SIGNAL', 'MEDICAL'].includes(su.unit_type)
          );
          subUnitsNeedingRole1.forEach((subUnit, subUnitIdx) => {
            const r1 = result.role1_facilities[role1Index];
            if (r1) {
              facilities.push({
                id: getFacilityId(r1, `role1_div_bde${brigadeIdx + 1}_${subUnit.id || subUnitIdx}`),
                name: generateFacilityDisplayName(
                  { ...r1, parent_battalion_name: subUnit.name, parent_brigade_name: bdeNameLocal },
                  'ROLE1', { battalionIdx: subUnitIdx, brigadeIdx }
                ),
                type: 'ROLE1',
                parent_unit: subUnit.name,
                parent_unit_type: subUnit.unit_type,
                parent_unit_id: subUnit.id || r1.parent_battalion_id,
                parent_brigade: bdeNameLocal,
                beds: r1.beds || getBedDefault('ROLE1'),
                personnel: r1.personnel || 15,
                has_surgical: false,
                hierarchy: { division: divisionName, brigade: bdeNameLocal, battalion: subUnit.name },
                sub_unit_strength: subUnit.strength,
                sub_unit_type: subUnit.unit_type,
                is_from_doctrinal_composition: true,
                organic_assets:    r1.organic_assets    || null,
                attached_assets:   r1.attached_assets   || [],
                detached_assets:   r1.detached_assets   || [],
                evacuation_config: r1.evacuation_config || null,
        activate_day:    r1.activate_day   ?? null,
        deactivate_day:  r1.deactivate_day ?? null,
        setup_hours:     r1.setup_hours    ?? null
              });
              role1Index++;
            }
          });

        } else {
          // Legacy: use battalion array
          const brigadeBattalions = battalions.filter(bn => bn.parent_brigade_id === brigade.id);
          const battalionCount = brigadeBattalions.length || 4;
          for (let bnIdx = 0; bnIdx < battalionCount; bnIdx++) {
            const r1 = result.role1_facilities[role1Index];
            if (r1) {
              const parentBattalion = brigadeBattalions[bnIdx];
              const bnName = parentBattalion?.name || `Battalion ${bnIdx + 1}`;
              facilities.push({
                id: getFacilityId(r1, `role1_div_bde${brigadeIdx + 1}_bn${bnIdx + 1}`),
                name: generateFacilityDisplayName(
                  { ...r1, parent_battalion_name: bnName, parent_brigade_name: bdeNameLocal },
                  'ROLE1', { battalionIdx: bnIdx, brigadeIdx }
                ),
                type: 'ROLE1',
                parent_unit: bnName,
                parent_unit_id: r1.parent_battalion_id || parentBattalion?.id,
                parent_brigade: bdeNameLocal,
                beds: r1.beds || getBedDefault('ROLE1'),
                personnel: r1.personnel || 15,
                has_surgical: false,
                hierarchy: { division: divisionName, brigade: bdeNameLocal, battalion: bnName },
                is_from_doctrinal_composition: false,
                organic_assets:    r1.organic_assets    || null,
                attached_assets:   r1.attached_assets   || [],
                detached_assets:   r1.detached_assets   || [],
                evacuation_config: r1.evacuation_config || null,
        activate_day:    r1.activate_day   ?? null,
        deactivate_day:  r1.deactivate_day ?? null,
        setup_hours:     r1.setup_hours    ?? null
              });
              role1Index++;
            }
          }
        }
      });

      // Role 2 Basic (from brigades)
      if (result.role2_facilities) {
        result.role2_facilities.forEach((r2, idx) => {
          const parentBrigade = brigades.find(bde => bde.id === r2.parent_brigade_id) || brigades[idx];
          const bdeName = parentBrigade?.name || r2.parent_brigade_name || `Brigade ${idx + 1}`;
          facilities.push({
            id: getFacilityId(r2, `role2_basic_div_bde${idx + 1}`),
            name: generateFacilityDisplayName(
              { ...r2, parent_brigade_name: bdeName, parent_division_name: divisionName },
              'ROLE2_BASIC', { brigadeIdx: idx }
            ),
            type: 'ROLE2_BASIC',
            parent_unit: bdeName,
            parent_unit_id: r2.parent_brigade_id,
            beds: r2.beds || getBedDefault('ROLE2B'),
            personnel: r2.personnel || 75,
            surgical_teams: r2.surgical_teams || 2,
            has_surgical: true,
            hierarchy: { division: divisionName, brigade: bdeName, battalion: null },
            organic_assets:    r2.organic_assets    || null,
            attached_assets:   r2.attached_assets   || [],
            detached_assets:   r2.detached_assets   || [],
            evacuation_config: r2.evacuation_config || null,
        activate_day:    r2.activate_day   ?? null,
        deactivate_day:  r2.deactivate_day ?? null,
        setup_hours:     r2.setup_hours    ?? null
          });
        });
      }

      // Role 2 Enhanced (division level) — check array first, then fallbacks
      if (result.role2_enhanced_facilities && result.role2_enhanced_facilities.length > 0) {
        result.role2_enhanced_facilities.forEach((r2e, idx) => {
          facilities.push({
            id: getFacilityId(r2e, `role2_enhanced_div_${idx}`),
            name: generateFacilityDisplayName(
              { ...r2e, parent_division_name: divisionName }, 'ROLE2_ENHANCED'
            ),
            type: 'ROLE2_ENHANCED',
            parent_unit: divisionName,
            parent_unit_id: result.division?.id,
            beds: r2e.beds || getBedDefault('ROLE2E'),
            personnel: r2e.personnel || 150,
            surgical_teams: r2e.surgical_teams || 4,
            icu_beds: r2e.icu_beds || 10,
            has_surgical: true,
            hierarchy: { division: divisionName, brigade: null, battalion: null },
            organic_assets:    r2e.organic_assets    || null,
            attached_assets:   r2e.attached_assets   || [],
            detached_assets:   r2e.detached_assets   || [],
            evacuation_config: r2e.evacuation_config || null,
        activate_day:    r2e.activate_day   ?? null,
        deactivate_day:  r2e.deactivate_day ?? null,
        setup_hours:     r2e.setup_hours    ?? null
          });
        });
      } else if (result.division?.role2_enhanced_facility) {
        const r2e = result.division.role2_enhanced_facility;
        facilities.push({
          id: getFacilityId(r2e, 'role2_enhanced_div'),
          name: generateFacilityDisplayName(
            { ...r2e, parent_division_name: divisionName }, 'ROLE2_ENHANCED'
          ),
          type: 'ROLE2_ENHANCED',
          parent_unit: divisionName,
          parent_unit_id: result.division?.id,
          beds: r2e.beds || getBedDefault('ROLE2E'),
          personnel: r2e.personnel || 150,
          surgical_teams: r2e.surgical_teams || 4,
          icu_beds: r2e.icu_beds || 10,
          has_surgical: true,
          hierarchy: { division: divisionName, brigade: null, battalion: null },
          organic_assets:    r2e.organic_assets    || null,
          attached_assets:   r2e.attached_assets   || [],
          detached_assets:   r2e.detached_assets   || [],
          evacuation_config: r2e.evacuation_config || null,
        activate_day:    r2e.activate_day   ?? null,
        deactivate_day:  r2e.deactivate_day ?? null,
        setup_hours:     r2e.setup_hours    ?? null
        });
      } else if (result.role2_enhanced_facility || result.role2_enhanced) {
        const r2e = result.role2_enhanced_facility || result.role2_enhanced;
        facilities.push({
          id: getFacilityId(r2e, 'role2_enhanced_div'),
          name: generateFacilityDisplayName(
            { ...r2e, parent_division_name: divisionName }, 'ROLE2_ENHANCED'
          ),
          type: 'ROLE2_ENHANCED',
          parent_unit: divisionName,
          parent_unit_id: result.division?.id,
          beds: r2e.beds || getBedDefault('ROLE2E'),
          personnel: r2e.personnel || 150,
          surgical_teams: r2e.surgical_teams || 4,
          icu_beds: r2e.icu_beds || 10,
          has_surgical: true,
          hierarchy: { division: divisionName, brigade: null, battalion: null },
          organic_assets:    r2e.organic_assets    || null,
          attached_assets:   r2e.attached_assets   || [],
          detached_assets:   r2e.detached_assets   || [],
          evacuation_config: r2e.evacuation_config || null,
        activate_day:    r2e.activate_day   ?? null,
        deactivate_day:  r2e.deactivate_day ?? null,
        setup_hours:     r2e.setup_hours    ?? null
        });
      }
    }

    // ---- CORPS: All facility types -------------------------------------------
    else if (echelon === 'CORPS') {
      const divisions  = result.divisions  || [];
      const brigades   = result.brigades   || [];
      const battalions = result.battalions || [];
      const corpsName  = result.corps?.name || 'Corps';

      // Role 1 — filter out CSS brigade Role 1s
      if (result.role1_facilities) {
        let filteredRole1Count = 0;
        result.role1_facilities.forEach((r1, idx) => {
          const parentBattalion = battalions.find(bn => bn.id === r1.parent_battalion_id);
          const parentBrigade   = brigades.find(bde =>
            bde.id === parentBattalion?.parent_brigade_id || bde.id === r1.parent_brigade_id
          );
          const parentDivision  = divisions.find(div =>
            div.id === parentBrigade?.parent_division_id || div.id === r1.parent_division_id
          );

          if (parentBrigade && !brigadeHasRole1(parentBrigade.unit_type)) {
            filteredRole1Count++;
            return;
          }

          const battalionName = parentBattalion?.name || r1.parent_battalion_name || `Battalion ${(idx % 4) + 1}`;
          const bdeName       = parentBrigade?.name   || r1.parent_brigade_name   || `Brigade ${Math.floor((idx % 12) / 4) + 1}`;
          const divisionName  = parentDivision?.name  || r1.parent_division_name  || `Division ${Math.floor(idx / 12) + 1}`;

          facilities.push({
            id: getFacilityId(r1, `role1_corps_d${Math.floor(idx/12)+1}_b${Math.floor((idx%12)/4)+1}_bn${(idx%4)+1}`),
            name: generateFacilityDisplayName(
              { ...r1, parent_battalion_name: battalionName, parent_brigade_name: bdeName, parent_division_name: divisionName },
              'ROLE1', { battalionIdx: idx % 4, brigadeIdx: Math.floor((idx % 12) / 4), divisionIdx: Math.floor(idx / 12) }
            ),
            type: 'ROLE1',
            parent_unit: sanitizeFacilityName(battalionName),
            parent_unit_id: r1.parent_battalion_id,
            parent_brigade: sanitizeFacilityName(bdeName),
            parent_division: sanitizeFacilityName(divisionName),
            beds: r1.beds || getBedDefault('ROLE1'),
            personnel: r1.personnel || 15,
            has_surgical: false,
            position_feba_km: r1.position_feba_km,
            hierarchy: {
              corps: sanitizeFacilityName(corpsName),
              division: sanitizeFacilityName(divisionName),
              brigade: sanitizeFacilityName(bdeName),
              battalion: sanitizeFacilityName(battalionName)
            },
            organic_assets:    r1.organic_assets    || null,
            attached_assets:   r1.attached_assets   || [],
            detached_assets:   r1.detached_assets   || [],
            evacuation_config: r1.evacuation_config || null,
        activate_day:    r1.activate_day   ?? null,
        deactivate_day:  r1.deactivate_day ?? null,
        setup_hours:     r1.setup_hours    ?? null
          });
        });
        debugLog(`Corps extraction: filtered out ${filteredRole1Count} Role 1 facilities from CSS brigades`);
      }

      // Role 2 Basic — filter out CSS brigade Role 2 Basics
      if (result.role2_facilities) {
        let filteredRole2Count = 0;
        result.role2_facilities.forEach((r2, idx) => {
          const parentBrigade  = brigades.find(bde => bde.id === r2.parent_brigade_id) || brigades[idx];
          const parentDivision = divisions.find(div =>
            div.id === parentBrigade?.parent_division_id || div.id === r2.parent_division_id
          );

          if (parentBrigade && !brigadeHasRole1(parentBrigade.unit_type)) {
            filteredRole2Count++;
            return;
          }

          const bdeName      = parentBrigade?.name  || r2.parent_brigade_name  || `Brigade ${(idx % 3) + 1}`;
          const divisionName = parentDivision?.name || r2.parent_division_name || `Division ${Math.floor(idx / 3) + 1}`;

          facilities.push({
            id: getFacilityId(r2, `role2_basic_corps_d${Math.floor(idx/3)+1}_b${(idx%3)+1}`),
            name: generateFacilityDisplayName(
              { ...r2, parent_brigade_name: bdeName, parent_division_name: divisionName },
              'ROLE2_BASIC', { brigadeIdx: idx % 3, divisionIdx: Math.floor(idx / 3) }
            ),
            type: 'ROLE2_BASIC',
            parent_unit: sanitizeFacilityName(bdeName),
            parent_unit_id: r2.parent_brigade_id,
            parent_division: sanitizeFacilityName(divisionName),
            beds: r2.beds || getBedDefault('ROLE2B'),
            personnel: r2.personnel || 75,
            surgical_teams: r2.surgical_teams || 2,
            has_surgical: true,
            position_feba_km: r2.position_feba_km,
            hierarchy: {
              corps: sanitizeFacilityName(corpsName),
              division: sanitizeFacilityName(divisionName),
              brigade: sanitizeFacilityName(bdeName),
              battalion: null
            },
            organic_assets:    r2.organic_assets    || null,
            attached_assets:   r2.attached_assets   || [],
            detached_assets:   r2.detached_assets   || [],
            evacuation_config: r2.evacuation_config || null,
        activate_day:    r2.activate_day   ?? null,
        deactivate_day:  r2.deactivate_day ?? null,
        setup_hours:     r2.setup_hours    ?? null
          });
        });
        debugLog(`Corps extraction: filtered out ${filteredRole2Count} Role 2 Basic facilities from CSS brigades`);
      }

      // Role 2 Enhanced (from divisions) — array first, then legacy
      if (result.role2_enhanced_facilities && result.role2_enhanced_facilities.length > 0) {
        result.role2_enhanced_facilities.forEach((r2e, idx) => {
          const parentDiv    = divisions.find(d => d.id === r2e.parent_division_id) || divisions[idx];
          const divisionName = parentDiv?.name || r2e.parent_division_name || `Division ${idx + 1}`;
          facilities.push({
            id: getFacilityId(r2e, `role2_enhanced_corps_d${idx + 1}`),
            name: generateFacilityDisplayName(
              { ...r2e, parent_division_name: divisionName, parent_corps_name: corpsName },
              'ROLE2_ENHANCED', { divisionIdx: idx }
            ),
            type: 'ROLE2_ENHANCED',
            parent_unit: divisionName,
            parent_unit_id: r2e.parent_division_id,
            parent_division: divisionName,
            beds: r2e.beds || getBedDefault('ROLE2E'),
            personnel: r2e.personnel || 150,
            surgical_teams: r2e.surgical_teams || 4,
            icu_beds: r2e.icu_beds || 10,
            has_surgical: true,
            position_feba_km: r2e.position_feba_km,
            hierarchy: { corps: corpsName, division: divisionName, brigade: null, battalion: null },
            organic_assets:    r2e.organic_assets    || null,
            attached_assets:   r2e.attached_assets   || [],
            detached_assets:   r2e.detached_assets   || [],
            evacuation_config: r2e.evacuation_config || null,
        activate_day:    r2e.activate_day   ?? null,
        deactivate_day:  r2e.deactivate_day ?? null,
        setup_hours:     r2e.setup_hours    ?? null
          });
        });
      } else if (divisions.length > 0) {
        // Legacy: check inside division objects
        divisions.forEach((div, idx) => {
          if (div.role2_enhanced_facility) {
            const r2e          = div.role2_enhanced_facility;
            const divisionName = div.name || `Division ${idx + 1}`;
            facilities.push({
              id: getFacilityId(r2e, `role2_enhanced_legacy_d${idx + 1}`),
              name: generateFacilityDisplayName(
                { ...r2e, parent_division_name: divisionName, parent_corps_name: corpsName },
                'ROLE2_ENHANCED', { divisionIdx: idx }
              ),
              type: 'ROLE2_ENHANCED',
              parent_unit: divisionName,
              parent_unit_id: div.id,
              beds: r2e.beds || getBedDefault('ROLE2E'),
              personnel: r2e.personnel || 150,
              surgical_teams: r2e.surgical_teams || 4,
              icu_beds: r2e.icu_beds || 10,
              has_surgical: true,
              hierarchy: { corps: corpsName, division: divisionName, brigade: null, battalion: null, legacy: true },
              organic_assets:    r2e.organic_assets    || null,
              attached_assets:   r2e.attached_assets   || [],
              detached_assets:   r2e.detached_assets   || [],
              evacuation_config: r2e.evacuation_config || null,
        activate_day:    r2e.activate_day   ?? null,
        deactivate_day:  r2e.deactivate_day ?? null,
        setup_hours:     r2e.setup_hours    ?? null
            });
          }
        });
      }

      // Role 3 — array first (handles Medical Brigade R3), then singular fallbacks
      if (result.role3_facilities && result.role3_facilities.length > 0) {
        result.role3_facilities.forEach((r3, idx) => {
          const isFromMedicalBrigade = r3.is_from_medical_brigade ||
            r3.parent_brigade_type === 'MEDICAL_BRIGADE' ||
            r3.facility_id?.includes('medbde');
          const sourceName = isFromMedicalBrigade
            ? (r3.parent_brigade_name || 'Medical Brigade')
            : corpsName;

          facilities.push({
            id: getFacilityId(r3, `role3_${isFromMedicalBrigade ? 'medbde' : 'corps'}_${idx + 1}`),
            name: generateFacilityDisplayName(
              { ...r3, parent_corps_name: corpsName, parent_brigade_name: isFromMedicalBrigade ? sourceName : null },
              'ROLE3'
            ),
            type: 'ROLE3',
            parent_unit: sourceName,
            parent_unit_id: r3.parent_brigade_id || result.corps?.id,
            beds: r3.beds || getBedDefault('ROLE3'),
            personnel: r3.personnel || 300,
            surgical_teams: r3.surgical_teams || 8,
            icu_beds: r3.icu_beds || 40,
            has_surgical: true,
            position_feba_km: r3.position_feba_km,
            is_from_medical_brigade: isFromMedicalBrigade,
            hierarchy: {
              corps: corpsName,
              division: null,
              brigade: isFromMedicalBrigade ? sourceName : null,
              battalion: null,
              source: isFromMedicalBrigade ? 'MEDICAL_BRIGADE' : 'CORPS'
            },
            organic_assets:    r3.organic_assets    || null,
            attached_assets:   r3.attached_assets   || [],
            detached_assets:   r3.detached_assets   || [],
            evacuation_config: r3.evacuation_config || null,
        activate_day:    r3.activate_day   ?? null,
        deactivate_day:  r3.deactivate_day ?? null,
        setup_hours:     r3.setup_hours    ?? null
          });
        });
      } else if (result.role3_facility) {
        const r3 = result.role3_facility;
        facilities.push({
          id: getFacilityId(r3, 'role3_corps'),
          name: generateFacilityDisplayName({ ...r3, parent_corps_name: corpsName }, 'ROLE3'),
          type: 'ROLE3',
          parent_unit: corpsName,
          parent_unit_id: result.corps?.id,
          beds: r3.beds || getBedDefault('ROLE3'),
          personnel: r3.personnel || 300,
          surgical_teams: r3.surgical_teams || 8,
          icu_beds: r3.icu_beds || 40,
          has_surgical: true,
          position_feba_km: r3.position_feba_km,
          hierarchy: { corps: corpsName, division: null, brigade: null, battalion: null },
          organic_assets:    r3.organic_assets    || null,
          attached_assets:   r3.attached_assets   || [],
          detached_assets:   r3.detached_assets   || [],
          evacuation_config: r3.evacuation_config || null,
        activate_day:    r3.activate_day   ?? null,
        deactivate_day:  r3.deactivate_day ?? null,
        setup_hours:     r3.setup_hours    ?? null
        });
      } else if (result.corps?.role3_corps_facility) {
        const r3 = result.corps.role3_corps_facility;
        facilities.push({
          id: getFacilityId(r3, 'role3_corps_legacy'),
          name: generateFacilityDisplayName({ ...r3, parent_corps_name: corpsName }, 'ROLE3'),
          type: 'ROLE3',
          parent_unit: corpsName,
          parent_unit_id: result.corps.id,
          beds: r3.beds || getBedDefault('ROLE3'),
          personnel: r3.personnel || 300,
          surgical_teams: r3.surgical_teams || 8,
          icu_beds: r3.icu_beds || 40,
          has_surgical: true,
          position_feba_km: r3.position_feba_km,
          hierarchy: { corps: corpsName, division: null, brigade: null, battalion: null, legacy: true },
          organic_assets:    r3.organic_assets    || null,
          attached_assets:   r3.attached_assets   || [],
          detached_assets:   r3.detached_assets   || [],
          evacuation_config: r3.evacuation_config || null,
        activate_day:    r3.activate_day   ?? null,
        deactivate_day:  r3.deactivate_day ?? null,
        setup_hours:     r3.setup_hours    ?? null
        });
      }

      // Role 4 (strategic)
      if (result.corps?.role4_facilities && result.corps.role4_facilities.length > 0) {
        result.corps.role4_facilities.forEach((r4, idx) => {
          facilities.push({
            id: getFacilityId(r4, `role4_strategic_${idx + 1}`),
            name: generateFacilityDisplayName(r4, 'ROLE4'),
            type: 'ROLE4',
            parent_unit: 'Strategic',
            parent_unit_id: r4.parent_corps_id,
            beds: r4.beds || getBedDefault('ROLE4'),
            personnel: r4.personnel || 750,
            surgical_teams: r4.surgical_teams || 20,
            has_surgical: true,
            hierarchy: { corps: corpsName, division: null, brigade: null, battalion: null },
            organic_assets:    r4.organic_assets    || null,
            attached_assets:   r4.attached_assets   || [],
            detached_assets:   r4.detached_assets   || [],
            evacuation_config: r4.evacuation_config || null,
        activate_day:    r4.activate_day   ?? null,
        deactivate_day:  r4.deactivate_day ?? null,
        setup_hours:     r4.setup_hours    ?? null
          });
        });
      }
    }

  } catch (error) {
    console.error('❌ extractMedicalFacilities error:', error, '| echelon:', forceData?.echelon);
  }

  return facilities;
}

/**
 * Calculate evacuation routing distances and times for a set of facilities.
 * For each facility, determines the receiving facility (one role up) and calculates:
 * - Effective ground distance (terrain-adjusted)
 * - Estimated evacuation time
 * - Golden hour compliance flag
 *
 * @param {Array}  facilities    - Array from extractMedicalFacilities
 * @param {string} defaultTerrain - Terrain type key (default: 'ROLLING_HILLS')
 * @returns {Array} Facilities with .evacuation property added to each
 */
export function calculateEvacuationDistances(facilities, defaultTerrain = 'ROLLING_HILLS') {
  if (!facilities || facilities.length === 0) return facilities;

  const role2bs = facilities.filter(f => f.type === 'ROLE2_BASIC');
  const role2es = facilities.filter(f => f.type === 'ROLE2_ENHANCED');
  const role3s  = facilities.filter(f => f.type === 'ROLE3');
  const role4s  = facilities.filter(f => f.type === 'ROLE4');

  return facilities.map(facility => {
    const position = facility.position_feba_km || getDefaultPositionForRole(facility.type);

    let receivingFacility = null;
    let transportMethod   = 'GROUND';

    switch (facility.type) {
      case 'ROLE1':
        receivingFacility = role2bs.find(r2b =>
          r2b.parent_brigade === facility.parent_brigade ||
          r2b.hierarchy?.brigade === facility.hierarchy?.brigade
        ) || role2bs[0];
        transportMethod = 'GROUND';
        break;

      case 'ROLE2_BASIC':
        receivingFacility = role2es.find(r2e =>
          r2e.parent_division === facility.parent_division ||
          r2e.hierarchy?.division === facility.hierarchy?.division
        ) || role2es[0] || role3s[0];
        transportMethod = 'MIXED';
        break;

      case 'ROLE2_ENHANCED':
        receivingFacility = role3s[0];
        transportMethod   = 'MIXED';
        break;

      case 'ROLE3':
        receivingFacility = role4s[0];
        transportMethod   = 'AIR';
        break;

      case 'ROLE4':
        receivingFacility = null;
        break;

      default:
        receivingFacility = null;
        transportMethod   = 'UNKNOWN';
        break;
    }

    let evacuation = null;

    if (receivingFacility) {
      const receivingPosition  = receivingFacility.position_feba_km || getDefaultPositionForRole(receivingFacility.type);
      const straightLineDistance = Math.abs(receivingPosition - position);
      const terrainConfig      = getTerrainFactor(defaultTerrain);
      const routingFactor      = terrainConfig.distance_modifier;
      const effectiveDistance  = Math.round(straightLineDistance * routingFactor);

      const timeResult = calculateGroundEvacuationTime(
        effectiveDistance,
        defaultTerrain,
        'PAVED_SECONDARY',
        'CLEAR',
        false
      );

      evacuation = {
        receiving_facility_id:   receivingFacility.id,
        receiving_facility_name: receivingFacility.name,
        receiving_facility_type: receivingFacility.type,
        distance_km:             effectiveDistance,
        straight_line_km:        straightLineDistance,
        terrain_type:            defaultTerrain,
        terrain_factor:          routingFactor,
        route_type:              'PAVED_SECONDARY',
        transport_method:        transportMethod,
        estimated_time_min:      timeResult.time_minutes,
        golden_hour_compliant:   timeResult.golden_hour_compliant,
        route_description:       `Via ${terrainConfig.label.toLowerCase()} terrain`,
        user_edited:             false
      };

    } else if (facility.type !== 'ROLE4') {
      // Orphaned facility — no receiving facility found
      evacuation = {
        receiving_facility_id:   null,
        receiving_facility_name: 'Not configured',
        receiving_facility_type: null,
        distance_km:             0,
        terrain_type:            defaultTerrain,
        route_type:              'NONE',
        estimated_time_min:      0,
        golden_hour_compliant:   false,
        warning:                 'No receiving facility configured'
      };
    }

    return {
      ...facility,
      position_feba_km: position,
      evacuation
    };
  });
}

/**
 * Recalculate a single facility's evacuation data after a user edit.
 * Used when the user manually adjusts distance, terrain, or route type in Tab3.
 *
 * @param {Object} facility      - Facility to update
 * @param {Object} newValues     - { distance_km, terrain_type, route_type }
 * @param {Array}  allFacilities - All facilities for receiving-facility lookup
 * @returns {Object} Updated facility with recalculated evacuation data
 */
export function recalculateFacilityEvacuation(facility, newValues, allFacilities) {
  const { distance_km, terrain_type, route_type } = newValues;

  const receivingFacility = allFacilities.find(f =>
    f.id === facility.evacuation?.receiving_facility_id
  );

  if (!receivingFacility) return facility;

  const effectiveRouteType   = route_type   || facility.evacuation?.route_type   || 'PAVED_SECONDARY';
  const effectiveTerrainType = terrain_type || facility.evacuation?.terrain_type || 'ROLLING_HILLS';

  const timeResult = calculateGroundEvacuationTime(
    distance_km,
    effectiveTerrainType,
    effectiveRouteType,
    'CLEAR',
    false
  );

  return {
    ...facility,
    evacuation: {
      ...facility.evacuation,
      distance_km,
      terrain_type:          effectiveTerrainType,
      route_type:            effectiveRouteType,
      estimated_time_min:    timeResult.time_minutes,
      golden_hour_compliant: timeResult.golden_hour_compliant,
      user_edited:           true
    }
  };
}

// ============================================================================
// DEFAULT EXPORT — all 6 public functions
// ============================================================================

// ESLint fix (Feb 13, 2026): Assign to variable before exporting
// Rule: import/no-anonymous-default-export
const MedicalService = {
  // v1.x — lightweight calculator routing
  extractMTFLocations,
  getEvacuationDestination,
  getSubordinateMTFs,
  // v2.0 — rich facility extraction for Tab2/Tab3
  extractMedicalFacilities,
  calculateEvacuationDistances,
  recalculateFacilityEvacuation
};

export default MedicalService;
